import React, { useState, useEffect } from 'react';
import { Heart, X, Gift, ChevronDown, ChevronUp } from 'lucide-react';
import { ShimmerButton } from './ShimmerButton';
import { DonationModal } from './DonationModal';
import { 
  isDonationOrbDayActive, 
  markDonationOrbSeen, 
  markDonationOrbDismissed,
  getSubscriptionInfo,
  getDonationOrbMessage,
  SubscriptionInfo
} from '../utils/subscription';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

export const DanaFloatingSymbol: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showCard, setShowCard] = useState(false);
  const [showDefinition, setShowDefinition] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({ isSubscribed: false });
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Check subscription status and whether to show orb
    const checkSubscriptionAndVisibility = () => {
      if (!user) {
        setShouldShow(false);
        return;
      }

      const subInfo = getSubscriptionInfo();
      setSubscriptionInfo(subInfo);

      const shouldShowOrb = isDonationOrbDayActive(subInfo);
      setShouldShow(shouldShowOrb);

      // Mark as seen when orb is shown
      if (shouldShowOrb) {
        markDonationOrbSeen();
      }
    };

    checkSubscriptionAndVisibility();
  }, [user]);

  const handleOrbClick = () => {
    setShowCard(true);
  };

  const handleDonateToManivo = () => {
    setShowCard(false); // Close the Dana card
    setShowDonationModal(true); // Open the donation modal
  };

  const handleDismissOrb = (e: React.MouseEvent) => {
    e.stopPropagation();
    markDonationOrbDismissed();
    setShouldShow(false);
  };

  // Don't render if user is not logged in, not subscribed, or not the right day
  if (!shouldShow) {
    return null;
  }

  const orbMessage = getDonationOrbMessage(subscriptionInfo);

  return (
    <>
      {/* Floating Dana Symbol */}
      <div className="fixed bottom-6 right-6 z-40">
        <div className="relative">
          {/* Dismiss button */}
          <button
            onClick={handleDismissOrb}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs z-10 transition-colors duration-200"
            title={t('dana.dismissForToday')}
          >
            <X size={12} />
          </button>

          <button
            onClick={handleOrbClick}
            className="dana-symbol-button group relative w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
          >
            {/* Sanskrit-inspired symbol */}
            <div className="dana-symbol text-white text-2xl font-bold transform group-hover:rotate-12 transition-transform duration-300">
              ॐ
            </div>
            
            {/* Floating particles animation */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="dana-particle dana-particle-1"></div>
              <div className="dana-particle dana-particle-2"></div>
              <div className="dana-particle dana-particle-3"></div>
            </div>
            
            {/* Enhanced Tooltip with subscription message */}
            <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="bg-black text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap max-w-64">
                {orbMessage}
              </div>
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
            </div>
          </button>

          {/* Celebration animation for special days */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="celebration-sparkle celebration-sparkle-1"></div>
            <div className="celebration-sparkle celebration-sparkle-2"></div>
            <div className="celebration-sparkle celebration-sparkle-3"></div>
          </div>
        </div>
      </div>

      {/* Dana Card Modal */}
      {showCard && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="dana-card bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-amber-100">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-t-2xl">
              <button
                onClick={() => setShowCard(false)}
                className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <X size={20} />
              </button>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-2xl font-bold">ॐ</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">{t('dana.dana')}</h2>
                <p className="text-sm text-amber-700 font-medium">{t('dana.sanskritPali')}</p>
                
                {/* Special milestone message */}
                {subscriptionInfo.subscriptionDate && (
                  <div className="mt-3 bg-amber-100 rounded-lg p-3">
                    <p className="text-amber-800 text-sm font-medium">
                      {orbMessage}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Collapsible Definition */}
              <div className="mb-6">
                <button
                  onClick={() => setShowDefinition(!showDefinition)}
                  className="w-full flex items-center justify-between bg-amber-50 hover:bg-amber-100 rounded-lg p-3 transition-colors duration-200 border border-amber-200"
                >
                  <div className="flex-1 text-left">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">{t('dana.definition')}</h3>
                    {!showDefinition && (
                      <p className="text-sm text-gray-600">
                        {t('dana.definitionPreview')}
                      </p>
                    )}
                  </div>
                  {showDefinition ? (
                    <ChevronUp size={18} className="text-amber-600 flex-shrink-0 ml-2" />
                  ) : (
                    <ChevronDown size={18} className="text-amber-600 flex-shrink-0 ml-2" />
                  )}
                </button>
                
                {showDefinition && (
                  <div className="mt-3 bg-amber-50 rounded-lg p-4 border border-amber-200 animate-in slide-in-from-top-2 duration-300">
                    <div className="space-y-2 text-gray-700 text-sm">
                      <p dangerouslySetInnerHTML={{ __html: t('dana.definition1') }} />
                      <p dangerouslySetInnerHTML={{ __html: t('dana.definition2') }} />
                      <p dangerouslySetInnerHTML={{ __html: t('dana.definition3') }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Practice Dana Section */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('dana.practiceDanaToday')}</h3>
                <p className="text-gray-600 text-sm mb-4">
                  {t('dana.embraceAbundanceSpirit')}
                </p>
              </div>

              {/* Donation Options */}
              <div className="space-y-4">
                {/* Support Manivo */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Heart size={16} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900 mb-1">{t('dana.supportManivo')}</h4>
                      <p className="text-gray-600 text-xs mb-3">
                        {t('dana.supportManivoDescription')}
                      </p>
                      <ShimmerButton
                        onClick={handleDonateToManivo}
                        shimmerColor="#ffffff"
                        shimmerDuration="2s"
                        background="linear-gradient(135deg, rgb(147 51 234) 0%, rgb(219 39 119) 100%)"
                        className="text-sm font-medium"
                      >
                        <Gift size={14} className="mr-2" />
                        {t('dana.blessManivo')}
                      </ShimmerButton>
                    </div>
                  </div>
                </div>

                {/* Wisdom Quote */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 text-center border border-amber-100">
                  <p className="text-gray-700 italic leading-relaxed text-sm">
                    {t('dana.buddhistQuote')}
                  </p>
                  <p className="text-xs text-amber-700 mt-2 font-medium">{t('dana.buddhistTeaching')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Donation Modal */}
      <DonationModal 
        isOpen={showDonationModal}
        onClose={() => setShowDonationModal(false)}
      />
    </>
  );
};